/**
 * Single home of the „do przypisania” guess (review 2026-10-07, PR #304): where
 * a ZK cargo line WITHOUT a saved pair is counted on a factory truck. The truck
 * card (transportLoadTable) shows it there with that line's freight, and the
 * form (cargoPairing) counts it into the default ZD quantity — both call this,
 * so they cannot disagree.
 *
 * Rule: lines of the row's variant only; first a line whose rest is exactly
 * the row (a ZD placed for this customer, reserved for it), then the others,
 * each taking min(need, rest), until the row is covered. Rows go in
 * `orderProductId` order and lines in id order — neither depends on the order
 * the API happens to list orders or ZD in.
 */
export type GuessUnit = 'pcs' | 'm2' | 'm3';
export type GuessSets = Record<GuessUnit, number>;

export interface GuessLine {
  id: string;
  variantId: string | null | undefined;
  /** The line's own unit, preferred when comparing. */
  unit: GuessUnit | null;
  /** What the line still has for unpaired rows, in every unit it knows. */
  remaining: GuessSets;
}

export interface GuessRow {
  key: string;
  variantId: string | null | undefined;
  need: GuessSets;
}

export interface GuessFill {
  lineId: string;
  /** The compared unit and the amount taken in it. */
  unit: GuessUnit;
  amount: number;
  /** Share of the line's remaining-before-this-fill, for proportional freight/weight. */
  taken: GuessSets;
}

export const GUESS_UNITS: GuessUnit[] = ['pcs', 'm2', 'm3'];
const EPS = 0.0005;

export function commonGuessUnit(preferred: GuessUnit | null, a: GuessSets, b: GuessSets): GuessUnit | null {
  const order = preferred ? [preferred, ...GUESS_UNITS.filter((unit) => unit !== preferred)] : GUESS_UNITS;
  return order.find((unit) => a[unit] > EPS && b[unit] > EPS) ?? null;
}

/**
 * @returns per row key the fills it got (empty = nothing left on any line);
 *          `left` = what of each row no line covers.
 */
export function guessUnpairedFills(
  rows: GuessRow[],
  lines: GuessLine[],
): { fills: Map<string, GuessFill[]>; left: Map<string, GuessSets> } {
  const ordered = [...rows].sort((a, b) => a.key.localeCompare(b.key));
  const open = [...lines]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((line) => ({ ...line, remaining: { ...line.remaining } }));
  const sameVariant = (row: GuessRow, line: GuessLine) => Boolean(row.variantId) && line.variantId === row.variantId;

  const reservedBy = new Map<string, string>();
  ordered.forEach((row) => {
    const exact = open.find((line) => !reservedBy.has(line.id) && sameVariant(row, line) && (() => {
      const unit = commonGuessUnit(line.unit, row.need, line.remaining);
      return unit !== null && Math.abs(line.remaining[unit] - row.need[unit]) <= EPS;
    })());
    if (exact) reservedBy.set(exact.id, row.key);
  });

  const fills = new Map<string, GuessFill[]>();
  const left = new Map<string, GuessSets>();
  ordered.forEach((row) => {
    const need = { ...row.need };
    const own = open.filter((line) => reservedBy.get(line.id) === row.key);
    const rest = open.filter((line) => !reservedBy.has(line.id));
    const rowFills: GuessFill[] = [];
    for (const line of [...own, ...rest]) {
      if (!sameVariant(row, line)) continue;
      const unit = commonGuessUnit(line.unit, need, line.remaining);
      if (!unit) continue;
      const amount = Math.min(need[unit], line.remaining[unit]);
      const lineRatio = amount / line.remaining[unit];
      const needRatio = amount / need[unit];
      const taken = Object.fromEntries(GUESS_UNITS.map((other) => [other, line.remaining[other] * lineRatio])) as GuessSets;
      GUESS_UNITS.forEach((other) => {
        line.remaining[other] = Math.max(0, line.remaining[other] - taken[other]);
        need[other] = Math.max(0, need[other] * (1 - needRatio));
      });
      rowFills.push({ lineId: line.id, unit, amount, taken });
      if (GUESS_UNITS.every((other) => need[other] <= EPS)) break;
    }
    fills.set(row.key, rowFills);
    left.set(row.key, need);
  });
  return { fills, left };
}
